import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { PageContainer } from "../../components/PageContainer";
import { useIsSuperAdmin } from "../../App";
import {
  BAND_COLOR_FIELDS,
  CLASHFINDER_COLOR_FIELDS,
  ColorFieldDef,
  HOME_COLOR_FIELDS,
  IMAGE_FIELDS,
  ImageFieldKey,
  LINEUP_COLOR_FIELDS,
  NAVIGATION_COLOR_FIELDS,
  PRIORITY_COLOR_KEYS,
} from "./schema";
import { isOverridden, resolveAll, resolveColor } from "./colorResolver";
import { ColorField } from "./ColorField";
import { ImageField } from "./ImageField";
import { PhoneFrame } from "./PhoneFrame";
import { HomePreview } from "./previews/HomePreview";
import { LineupPreview } from "./previews/LineupPreview";
import { ClashfinderPreview } from "./previews/ClashfinderPreview";
import { NavigationPreview } from "./previews/NavigationPreview";
import { BandPreview } from "./previews/BandPreview";
import { exportTheme } from "./exportTheme";

type ScreenKey = "home" | "navigation" | "lineup" | "clashfinder" | "band";

const SCREENS: { key: ScreenKey; label: string; fields: ColorFieldDef[] }[] = [
  { key: "home", label: "Home", fields: HOME_COLOR_FIELDS },
  { key: "navigation", label: "Nav Menu", fields: NAVIGATION_COLOR_FIELDS },
  { key: "lineup", label: "Lineup", fields: LINEUP_COLOR_FIELDS },
  { key: "clashfinder", label: "Clashfinder", fields: CLASHFINDER_COLOR_FIELDS },
  { key: "band", label: "Band", fields: BAND_COLOR_FIELDS },
];

const groupBySection = (fields: ColorFieldDef[]) => {
  const sections: { section: string; fields: ColorFieldDef[] }[] = [];
  for (const field of fields) {
    let group = sections.find((s) => s.section === field.section);
    if (!group) {
      group = { section: field.section, fields: [] };
      sections.push(group);
    }
    group.fields.push(field);
  }
  return sections;
};

type ImageEntry = { file: File; url: string };

const ThemeEditor = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const [activeScreen, setActiveScreen] = useState<ScreenKey>("home");
  const [exporting, setExporting] = useState(false);

  const [homeOverrides, setHomeOverrides] = useState<Record<string, string>>({});
  const [lineupOverrides, setLineupOverrides] = useState<Record<string, string>>({});
  const [clashfinderOverrides, setClashfinderOverrides] = useState<Record<string, string>>({});

  const [images, setImages] = useState<Partial<Record<ImageFieldKey, ImageEntry>>>({});

  const overridesByScreen: Record<ScreenKey, Record<string, string>> = {
    home: homeOverrides,
    navigation: homeOverrides,
    band: homeOverrides,
    lineup: lineupOverrides,
    clashfinder: clashfinderOverrides,
  };
  const setOverridesByScreen: Record<ScreenKey, (v: Record<string, string>) => void> = {
    home: setHomeOverrides,
    navigation: setHomeOverrides,
    band: setHomeOverrides,
    lineup: setLineupOverrides,
    clashfinder: setClashfinderOverrides,
  };

  const activeScreenDef = SCREENS.find((s) => s.key === activeScreen)!;
  const activeOverrides = overridesByScreen[activeScreen];
  const setActiveOverrides = setOverridesByScreen[activeScreen];

  const setFieldValue = (key: string, value: string) => {
    setActiveOverrides({ ...activeOverrides, [key]: value });
  };
  const resetField = (key: string) => {
    const next = { ...activeOverrides };
    delete next[key];
    setActiveOverrides(next);
  };
  const resetVisibleFields = () => {
    const next = { ...activeOverrides };
    activeScreenDef.fields.forEach((field) => delete next[field.key]);
    setActiveOverrides(next);
  };

  const handleImageChange = (key: ImageFieldKey, file: File) => {
    const url = URL.createObjectURL(file);
    setImages((prev) => {
      const existing = prev[key];
      if (existing) URL.revokeObjectURL(existing.url);
      return { ...prev, [key]: { file, url } };
    });
  };
  const handleImageClear = (key: ImageFieldKey) => {
    setImages((prev) => {
      const existing = prev[key];
      if (existing) URL.revokeObjectURL(existing.url);
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTheme({ homeOverrides, lineupOverrides, clashfinderOverrides, images });
    } finally {
      setExporting(false);
    }
  };

  const homeResolved = useMemo(() => resolveAll(HOME_COLOR_FIELDS, homeOverrides), [homeOverrides]);
  const lineupResolved = useMemo(
    () => resolveAll(LINEUP_COLOR_FIELDS, lineupOverrides),
    [lineupOverrides]
  );
  const clashfinderResolved = useMemo(
    () => resolveAll(CLASHFINDER_COLOR_FIELDS, clashfinderOverrides),
    [clashfinderOverrides]
  );

  const priorityColors = PRIORITY_COLOR_KEYS.map((key) =>
    resolveColor(HOME_COLOR_FIELDS, homeOverrides, key)
  );

  if (!isSuperAdmin) {
    return <Navigate to="/landing" replace />;
  }

  const sections = groupBySection(activeScreenDef.fields);
  const hasAnyOverrides = activeScreenDef.fields.some(
    (field) => !field.derivedFrom && Boolean(activeOverrides[field.key])
  );

  return (
    <PageContainer
      title="Theme Editor"
      actions={
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export theme"}
        </Button>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Preview and override this org&apos;s theme colors and branding images. Nothing here is
        saved to the org yet — use Export to download a zip of colors.json, lineup_colors.json,
        clashfinder_colors.json and any images you&apos;ve replaced.
      </Typography>

      <Tabs value={activeScreen} onChange={(_, v) => setActiveScreen(v)} sx={{ mb: 2 }}>
        {SCREENS.map((screen) => (
          <Tab key={screen.key} value={screen.key} label={screen.label} />
        ))}
      </Tabs>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>Branding Images</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
                {IMAGE_FIELDS.map((field) => (
                  <ImageField
                    key={field.key}
                    field={field}
                    previewUrl={images[field.key]?.url}
                    onChange={(file) => handleImageChange(field.key, file)}
                    onClear={() => handleImageClear(field.key)}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3, mb: 1 }}>
            <Typography fontWeight={700}>{activeScreenDef.label} Colors</Typography>
            <Button size="small" disabled={!hasAnyOverrides} onClick={resetVisibleFields}>
              Reset all
            </Button>
          </Stack>

          {sections.map((group) => (
            <Accordion key={group.section} defaultExpanded={group.section === "Base Theme"}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{group.section}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
                  {group.fields.map((field) => (
                    <ColorField
                      key={field.key}
                      label={field.label}
                      value={resolveColor(activeScreenDef.fields, activeOverrides, field.key)}
                      isOverridden={isOverridden(activeScreenDef.fields, activeOverrides, field.key)}
                      defaultValueLabel={
                        field.fallback.type === "literal"
                          ? field.fallback.value
                          : activeScreenDef.fields.find((f) => f.key === (field.fallback as { key: string }).key)
                              ?.label ?? (field.fallback as { key: string }).key
                      }
                      helperText={field.helperText}
                      disabled={Boolean(field.derivedFrom)}
                      onChange={(value) => setFieldValue(field.key, value)}
                      onReset={() => resetField(field.key)}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 }, alignSelf: "flex-start" }}>
          <PhoneFrame>
            {activeScreen === "home" && (
              <HomePreview
                colors={homeResolved}
                images={{
                  home_background: images.home_background?.url,
                  home_logo: images.home_logo?.url,
                  placeholder: images.placeholder?.url,
                }}
              />
            )}
            {activeScreen === "navigation" && (
              <NavigationPreview
                colors={homeResolved}
                images={{
                  menu_background: images.menu_background?.url,
                  menu_logo: images.menu_logo?.url,
                  home_background: images.home_background?.url,
                  home_logo: images.home_logo?.url,
                  placeholder: images.placeholder?.url,
                }}
              />
            )}
            {activeScreen === "lineup" && (
              <LineupPreview
                colors={lineupResolved}
                images={{ thumbnail: images.thumbnail?.url }}
                likedStarColor={priorityColors[1]}
              />
            )}
            {activeScreen === "clashfinder" && (
              <ClashfinderPreview colors={clashfinderResolved} priorityColors={priorityColors} />
            )}
            {activeScreen === "band" && (
              <BandPreview
                colors={homeResolved}
                images={{ placeholder: images.placeholder?.url }}
                likedStarColor={priorityColors[2]}
              />
            )}
          </PhoneFrame>
        </Box>
      </Stack>
    </PageContainer>
  );
};

export default ThemeEditor;
